import { formatUtcOffset } from '@felix/domain';
import {
  Button,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  Skeleton,
  Textarea,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendarTime,
  IconCheck,
  IconClockHour4,
  IconHourglass,
  IconInfoCircleFilled,
  IconPlus,
  IconWorld,
  IconX,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { EmptyState } from '../../EmptyState.js';
import { DayInput } from '../../inputs/DayInput.js';
import { TimeInput } from '../../inputs/TimeInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { EMPTY, RestNotice, restTable } from '../personnel/rest-ui.js';
import type { ClockChangeView } from '../personnel/ship-clock.js';
import classes from './ShipClockScreen.module.css';
import type { ShipClockData, ShipClockSource } from './source.js';
import {
  approvable,
  cancellable,
  changeDayMinutes,
  clockDraft,
  effectiveText,
  hoursText,
  planSchedule,
  signedMinutes,
  utcText,
} from './view.js';

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; data: ShipClockData };

const STATUS_TONE: Readonly<Record<ClockChangeView['status'], StatusTone>> = {
  pending: 'warning',
  approved: 'success',
  cancelled: 'neutral',
};

const errorCode = (error: unknown) => {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : 'GENERIC';
};

/**
 * Gemi Saati Kaydı (Ship's Clock Log; M18, ADR 0065 §9): saat değişikliklerinin listesi, gemide
 * yeni değişiklik (vardiya zabiti), kaptan onayı (parolalı) ve iptal; ofiste salt okunur. Görsel
 * dil dinlenme saatleri ekranıyla aynı (tablo çerçevesi, rozetler, bildirim kutuları).
 */
export function ShipClockScreen({ source }: { source: ShipClockSource }) {
  const { t } = useTranslation();
  const [vesselId, setVesselId] = useState<string | undefined>(undefined);
  const [state, setState] = useState<Load>({ kind: 'loading' });
  const [revision, setRevision] = useState(0);
  const [creating, setCreating] = useState(false);
  const [approving, setApproving] = useState<ClockChangeView | null>(null);
  const [cancelling, setCancelling] = useState<ClockChangeView | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `revision` yazmadan sonra yeniden okutur
  useEffect(() => {
    let cancelled = false;
    source
      .load(vesselId ? { vesselId } : {})
      .then((data) => {
        if (!cancelled) setState({ kind: 'ready', data });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [source, vesselId, revision]);
  const reload = useCallback(() => setRevision((n) => n + 1), []);

  const data = state.kind === 'ready' ? state.data : null;
  const now = data ? Date.parse(data.now) : Date.now();
  const changes = useMemo(
    () =>
      data ? [...data.changes].sort((a, b) => b.effectiveAt.localeCompare(a.effectiveAt)) : [],
    [data],
  );
  const pending = changes.filter((c) => c.status === 'pending');
  const upcoming = changes.filter(
    (c) =>
      c.status !== 'cancelled' &&
      Date.parse(c.effectiveAt) > now &&
      Date.parse(c.effectiveAt) <= now + 48 * 3_600_000,
  );
  const minutesText = (value: number) => t('shipClock.minutes', { value });
  /** Günün süresi rozeti: "25 h day" / "25 saatlik gün" (dinlenme ekranıyla aynı metin). */
  const dayText = (minutes: number) =>
    t('restHours.clock.dayLength', {
      hours: minutes % 60 === 0 ? minutes / 60 : hoursText(minutes).replace(' h', ''),
    });
  const changeLabel = (change: ClockChangeView) =>
    t(`shipClock.change.${change.changeMin < 0 ? 'retard' : 'advance'}`, {
      minutes: minutesText(Math.abs(change.changeMin)),
    });
  const noticeText = (change: ClockChangeView, key: 'pending' | 'will') => {
    const wall = effectiveText(change);
    const values = {
      date: wall.slice(0, 10),
      time: wall.slice(11),
      minutes: Math.abs(change.changeMin),
      from: formatUtcOffset(change.oldOffsetMin),
      to: formatUtcOffset(change.newOffsetMin),
    };
    return key === 'pending'
      ? t('restHours.clock.pending', values)
      : t(`restHours.clock.${change.changeMin < 0 ? 'willRetard' : 'willAdvance'}`, values);
  };

  const editor = source.editor;
  const header = (
    <PageHeader
      title={t('shipClock.title')}
      description={t('shipClock.subtitle')}
      actions={
        <span className={classes.actions}>
          {data?.vessels && data.vessels.length > 0 ? (
            <Select
              aria-label={t('shipClock.vessel')}
              data={data.vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={data.vessel.id}
              onChange={(value) => {
                if (value) setVesselId(value);
              }}
              allowDeselect={false}
              w={220}
            />
          ) : null}
          {editor && data?.can.create ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
              {t('shipClock.newChange')}
            </Button>
          ) : null}
        </span>
      }
    />
  );

  if (state.kind === 'error') {
    return (
      <div className={classes.screen}>
        {header}
        <EmptyState
          tone="danger"
          title={t('shipClock.loadError')}
          description={t(`shipClock.errors.${state.code}`, {
            defaultValue: t('shipClock.errors.GENERIC'),
          })}
          action={
            <Button size="xs" variant="light" onClick={reload}>
              {t('common.retry')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className={classes.screen}>
      {header}
      <section className={classes.tiles} aria-label={t('shipClock.summary.label')}>
        {data ? (
          <>
            <StatTile
              icon={<IconClockHour4 size={20} />}
              label={t('shipClock.now')}
              value={formatUtcOffset(data.currentOffsetMin)}
            />
            <StatTile
              icon={<IconWorld size={20} />}
              label={t('shipClock.baseZone')}
              value={data.vessel.timeZone}
            />
            <StatTile
              icon={<IconHourglass size={20} />}
              label={t('shipClock.summary.pending')}
              value={pending.length}
              tone={pending.length > 0 ? 'warning' : 'default'}
            />
            <StatTile
              icon={<IconCalendarTime size={20} />}
              label={t('shipClock.summary.upcoming')}
              value={upcoming.length}
            />
          </>
        ) : (
          [0, 1, 2, 3].map((n) => <Skeleton key={n} height={72} radius={8} />)
        )}
      </section>

      {pending.map((change) => (
        <RestNotice
          key={`pending-${change.id}`}
          tone="warning"
          icon={<IconAlertTriangle size={18} />}
          role="status"
        >
          {noticeText(change, 'pending')}
        </RestNotice>
      ))}
      {upcoming
        .filter((c) => c.status === 'approved')
        .map((change) => (
          <RestNotice
            key={`upcoming-${change.id}`}
            tone="info"
            icon={<IconInfoCircleFilled size={18} />}
            role="status"
          >
            {noticeText(change, 'will')}
          </RestNotice>
        ))}

      <div className={classes.body}>
        <Panel title={t('shipClock.list')} className={classes.listPanel}>
          <section
            className={restTable.frame}
            aria-label={t('shipClock.list')}
            // Yatay kayan tablo klavyeyle de kaydırılabilsin (WCAG 2.1.1)
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge
            tabIndex={0}
          >
            <table className={restTable.table}>
              <thead>
                <tr>
                  <th scope="col">{t('shipClock.columns.effective')}</th>
                  <th scope="col">{t('shipClock.columns.change')}</th>
                  <th scope="col">{t('shipClock.columns.offsets')}</th>
                  <th scope="col">{t('shipClock.columns.reason')}</th>
                  <th scope="col">{t('shipClock.columns.status')}</th>
                  {editor ? (
                    <th scope="col">
                      <span className={classes.hidden}>{t('shipClock.columns.actions')}</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {data === null ? (
                  [0, 1, 2].map((n) => (
                    <tr key={n} aria-hidden>
                      <td colSpan={editor ? 6 : 5}>
                        <Skeleton height={10} />
                      </td>
                    </tr>
                  ))
                ) : changes.length === 0 ? (
                  <tr>
                    <td colSpan={editor ? 6 : 5} className={classes.emptyCell}>
                      <EmptyState
                        icon={<IconClockHour4 size={22} />}
                        title={t('shipClock.empty')}
                        description={t('shipClock.emptyText', { zone: data.vessel.timeZone })}
                      />
                    </td>
                  </tr>
                ) : (
                  changes.map((change) => {
                    const day = changeDayMinutes(data.vessel.timeZone, data.changes, change);
                    return (
                      <tr key={change.id} data-status={change.status}>
                        <td className={restTable.num}>
                          {effectiveText(change)}
                          <small className={classes.byline}>{utcText(change.effectiveAt)}</small>
                        </td>
                        <td>
                          <span className={classes.change}>
                            <strong className={restTable.num}>
                              {t('shipClock.minutes', { value: signedMinutes(change.changeMin) })}
                            </strong>
                            <span className={`${restTable.secondary} ${classes.changeText}`}>
                              {changeLabel(change)}
                            </span>
                            {day !== 1440 && change.status !== 'cancelled' ? (
                              <span className={classes.dayBadge}>{dayText(day)}</span>
                            ) : null}
                          </span>
                        </td>
                        <td className={restTable.num}>
                          {formatUtcOffset(change.oldOffsetMin)} →{' '}
                          {formatUtcOffset(change.newOffsetMin)}
                        </td>
                        <td className={`${restTable.wrap} ${classes.reason}`}>
                          {change.reason}
                          {change.status === 'cancelled' && change.cancelReason ? (
                            <small className={classes.cancelNote}>
                              {t('shipClock.cancelledBy', {
                                name: change.cancelledByName ?? EMPTY,
                                reason: change.cancelReason,
                              })}
                            </small>
                          ) : null}
                        </td>
                        <td>
                          <StatusBadge tone={STATUS_TONE[change.status]}>
                            {t(`shipClock.status.${change.status}`)}
                          </StatusBadge>
                          {/* Kaydeden ve onaylayan (değiştirilemez denetim izi) */}
                          <small className={classes.byline}>
                            {t('shipClock.recordedBy', { name: change.createdByName ?? EMPTY })}
                          </small>
                          {change.approvedByName ? (
                            <small className={classes.byline}>
                              {t('shipClock.approvedBy', { name: change.approvedByName })}
                            </small>
                          ) : null}
                        </td>
                        {editor ? (
                          <td className={classes.rowActions}>
                            {data.can.approve && approvable(data.changes, change) ? (
                              <Button
                                size="compact-sm"
                                leftSection={<IconCheck size={14} />}
                                onClick={() => setApproving(change)}
                              >
                                {t('shipClock.approve.action')}
                              </Button>
                            ) : null}
                            {cancellable(data, change) ? (
                              <Button
                                size="compact-sm"
                                variant="subtle"
                                color="red"
                                leftSection={<IconX size={14} />}
                                onClick={() => setCancelling(change)}
                              >
                                {t('shipClock.cancel.action')}
                              </Button>
                            ) : null}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </section>
          {!editor ? <p className={classes.readOnly}>{t('shipClock.readOnly')}</p> : null}
        </Panel>

        <Panel title={t('shipClock.info.title')} className={classes.infoPanel}>
          <p className={classes.infoText}>{t('shipClock.info.text')}</p>
          {data ? (
            <p className={classes.infoText}>
              <strong>{t('shipClock.baseZone')}:</strong> {data.vessel.timeZone} —{' '}
              {t('shipClock.baseZoneHint')}
            </p>
          ) : null}
        </Panel>
      </div>

      {editor && data ? (
        <>
          <NewChangeDialog
            opened={creating}
            data={data}
            onClose={() => setCreating(false)}
            onSaved={() => {
              setCreating(false);
              reload();
            }}
            record={editor.record}
          />
          <ApproveDialog
            change={approving}
            onClose={() => setApproving(null)}
            onDone={() => {
              setApproving(null);
              reload();
            }}
            approve={editor.approve}
          />
          <CancelDialog
            change={cancelling}
            onClose={() => setCancelling(null)}
            onDone={() => {
              setCancelling(null);
              reload();
            }}
            cancel={editor.cancel}
          />
        </>
      ) : null}
    </div>
  );
}

const shipDay = (iso: string, offset: number) =>
  new Date(Date.parse(iso) + offset * 60_000).toISOString();

function NewChangeDialog({
  opened,
  data,
  onClose,
  onSaved,
  record,
}: {
  opened: boolean;
  data: ShipClockData;
  onClose: () => void;
  onSaved: () => void;
  record: NonNullable<ShipClockSource['editor']>['record'];
}) {
  const { t } = useTranslation();
  const local = shipDay(data.now, data.currentOffsetMin);
  const [date, setDate] = useState<string | null>(local.slice(0, 10));
  const [time, setTime] = useState('00:00');
  const [direction, setDirection] = useState<'retard' | 'advance'>('retard');
  const [minutes, setMinutes] = useState<number | string>(60);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setDate(shipDay(data.now, data.currentOffsetMin).slice(0, 10));
    setTime('00:00');
    setDirection('retard');
    setMinutes(60);
    setReason('');
    setError(null);
    setTouched(false);
  }, [opened, data.now, data.currentOffsetMin]);

  const plan = useMemo(
    () => planSchedule(data.vessel.timeZone, data.changes),
    [data.vessel.timeZone, data.changes],
  );
  const draft = clockDraft(
    { date: date ?? '', time, direction, minutes: Number(minutes) || 0, reason },
    plan,
    data.now,
  );
  const blocking = draft.ok ? draft.errors.filter((e) => e !== 'REASON_REQUIRED' || touched) : [];
  const message = (code: string) =>
    t(`shipClock.errors.${code}`, { defaultValue: t('shipClock.errors.GENERIC') });
  const dayText = (minutes: number) =>
    t('restHours.clock.dayLength', {
      hours: minutes % 60 === 0 ? minutes / 60 : hoursText(minutes).replace(' h', ''),
    });
  const applySuggestion = (offset: number) => {
    if (!draft.ok) return;
    const change = offset - draft.oldOffsetMin;
    if (change === 0) return;
    setDirection(change < 0 ? 'retard' : 'advance');
    setMinutes(Math.abs(change));
  };

  const submit = async () => {
    setTouched(true);
    if (!draft.ok || draft.errors.length > 0) return;
    setBusy(true);
    setError(null);
    try {
      await record({
        effectiveAt: draft.effectiveAt,
        newOffsetMin: draft.newOffsetMin,
        oldOffsetMin: draft.oldOffsetMin,
        reason: reason.trim(),
      });
      onSaved();
    } catch (failure) {
      setError(errorCode(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('shipClock.form.title')}
      size={620}
      closeButtonProps={{ 'aria-label': t('shipClock.close') }}
    >
      <div className={classes.form}>
        <DayInput label={t('shipClock.form.date')} value={date} onChange={setDate} />
        <TimeInput
          label={t('shipClock.form.time')}
          hoursInputLabel={`${t('shipClock.form.time')} — ${t('common.hours')}`}
          minutesInputLabel={`${t('shipClock.form.time')} — ${t('common.minutes')}`}
          value={time}
          onChange={setTime}
        />
        <div className={classes.full}>
          <span className={classes.fieldLabel} id="clock-direction">
            {t('shipClock.form.direction')}
          </span>
          <SegmentedControl
            aria-labelledby="clock-direction"
            fullWidth
            value={direction}
            onChange={(value) => setDirection(value as 'retard' | 'advance')}
            data={[
              { value: 'retard', label: t('shipClock.form.retard') },
              { value: 'advance', label: t('shipClock.form.advance') },
            ]}
          />
        </div>
        <NumberInput
          label={t('shipClock.form.amount')}
          description={t('shipClock.form.amountHint')}
          min={1}
          max={1560}
          step={10}
          allowDecimal={false}
          value={minutes}
          onChange={setMinutes}
        />
        <div>
          <span className={classes.fieldLabel}>{t('shipClock.form.newOffset')}</span>
          <p className={classes.offsetValue}>
            {draft.ok
              ? `${formatUtcOffset(draft.oldOffsetMin)} → ${formatUtcOffset(draft.newOffsetMin)}`
              : EMPTY}
          </p>
        </div>
        <Textarea
          className={classes.full}
          label={t('shipClock.form.reason')}
          placeholder={t('shipClock.form.reasonPlaceholder')}
          autosize
          minRows={2}
          maxRows={4}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          onBlur={() => setTouched(true)}
          error={
            touched && draft.ok && draft.errors.includes('REASON_REQUIRED')
              ? message('REASON_REQUIRED')
              : undefined
          }
        />
      </div>

      {data.suggestions.length > 0 ? (
        <div className={classes.suggestions}>
          <p className={classes.fieldLabel}>{t('shipClock.form.suggestions')}</p>
          <ul>
            {data.suggestions.map((s) => (
              <li key={`${s.source}-${s.timeZone ?? ''}-${s.offsetMin}`}>
                <span>
                  {s.source === 'port'
                    ? t('shipClock.form.suggestionPort', {
                        label: s.label,
                        zone: s.timeZone,
                        offset: formatUtcOffset(s.offsetMin),
                      })
                    : t('shipClock.form.suggestionZone', {
                        label: s.label,
                        offset: formatUtcOffset(s.offsetMin),
                      })}
                </span>
                <Button
                  size="compact-xs"
                  variant="light"
                  disabled={!draft.ok || draft.oldOffsetMin === s.offsetMin}
                  onClick={() => applySuggestion(s.offsetMin)}
                >
                  {t('shipClock.form.use')}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {draft.ok ? (
        <RestNotice tone="info" icon={<IconClockHour4 size={18} />} role="status">
          {t('shipClock.form.preview', {
            time,
            after: draft.after,
            from: formatUtcOffset(draft.oldOffsetMin),
            to: formatUtcOffset(draft.newOffsetMin),
            day: dayText(draft.dayMinutes),
          })}
        </RestNotice>
      ) : (
        <RestNotice tone="danger" icon={<IconAlertTriangle size={18} />} role="alert">
          {message(draft.code)}
        </RestNotice>
      )}
      {blocking
        .filter((e) => e !== 'REASON_REQUIRED')
        .map((code) => (
          <RestNotice key={code} tone="danger" icon={<IconAlertTriangle size={18} />} role="alert">
            {message(code)}
          </RestNotice>
        ))}
      {error ? (
        <RestNotice tone="danger" icon={<IconAlertTriangle size={18} />} role="alert">
          {message(error)}
        </RestNotice>
      ) : null}
      <p className={classes.hint}>{t('shipClock.form.approvalNote')}</p>
      <p className={classes.hint}>{t('shipClock.form.midnightHint')}</p>

      <div className={classes.footer}>
        <Button variant="default" onClick={onClose}>
          {t('shipClock.form.cancel')}
        </Button>
        <Button loading={busy} onClick={() => void submit()} disabled={!draft.ok}>
          {t('shipClock.form.save')}
        </Button>
      </div>
    </Modal>
  );
}

function ApproveDialog({
  change,
  onClose,
  onDone,
  approve,
}: {
  change: ClockChangeView | null;
  onClose: () => void;
  onDone: () => void;
  approve: NonNullable<ShipClockSource['editor']>['approve'];
}) {
  const { t } = useTranslation();
  // Kaptan onayı açık onay adımıdır; parola yalnız şirket ayarı açıkken (ADR 0065 §2, ADR 0075)
  const reauth = useReauth('ship_clock_change');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (change) {
      reauth.reset();
      setError(null);
    }
  }, [change, reauth.reset]);
  const submit = async () => {
    if (!change || reauth.missing) return;
    setBusy(true);
    setError(null);
    try {
      await approve(change.id, reauth.body().password);
      onDone();
    } catch (failure) {
      reauth.noteError(failure);
      setError(errorCode(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      opened={change !== null}
      onClose={onClose}
      title={t('shipClock.approve.title')}
      closeButtonProps={{ 'aria-label': t('shipClock.close') }}
    >
      {change ? (
        <>
          <p className={classes.dialogText}>
            {t('shipClock.approve.text', {
              time: effectiveText(change),
              from: formatUtcOffset(change.oldOffsetMin),
              to: formatUtcOffset(change.newOffsetMin),
            })}
          </p>
          <p className={classes.dialogQuote}>{change.reason}</p>
          <ReauthField reauth={reauth} label={t('shipClock.password')} />
          {error ? (
            <RestNotice tone="danger" icon={<IconAlertTriangle size={18} />} role="alert">
              {t(`shipClock.errors.${error}`, { defaultValue: t('shipClock.errors.GENERIC') })}
            </RestNotice>
          ) : null}
          <div className={classes.footer}>
            <Button variant="default" onClick={onClose}>
              {t('shipClock.form.cancel')}
            </Button>
            <Button
              loading={busy}
              disabled={reauth.missing}
              leftSection={<IconCheck size={16} />}
              onClick={() => void submit()}
            >
              {t('shipClock.approve.confirm')}
            </Button>
          </div>
        </>
      ) : null}
    </Modal>
  );
}

function CancelDialog({
  change,
  onClose,
  onDone,
  cancel,
}: {
  change: ClockChangeView | null;
  onClose: () => void;
  onDone: () => void;
  cancel: NonNullable<ShipClockSource['editor']>['cancel'];
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (change) {
      setReason('');
      setError(null);
    }
  }, [change]);
  const submit = async () => {
    if (!change) return;
    if (reason.trim() === '') {
      setError('REASON_REQUIRED');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await cancel(change.id, reason.trim());
      onDone();
    } catch (failure) {
      setError(errorCode(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      opened={change !== null}
      onClose={onClose}
      title={t('shipClock.cancel.title')}
      closeButtonProps={{ 'aria-label': t('shipClock.close') }}
    >
      {change ? (
        <>
          <p className={classes.dialogText}>{t('shipClock.cancel.text')}</p>
          <Textarea
            label={t('shipClock.cancel.reason')}
            required
            autosize
            minRows={2}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
          {error ? (
            <RestNotice tone="danger" icon={<IconAlertTriangle size={18} />} role="alert">
              {t(`shipClock.errors.${error}`, { defaultValue: t('shipClock.errors.GENERIC') })}
            </RestNotice>
          ) : null}
          <div className={classes.footer}>
            <Button variant="default" onClick={onClose}>
              {t('shipClock.form.cancel')}
            </Button>
            <Button color="red" loading={busy} onClick={() => void submit()}>
              {t('shipClock.cancel.confirm')}
            </Button>
          </div>
        </>
      ) : null}
    </Modal>
  );
}
